import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Alert, CircularProgress } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { changePassword, userPageLoaded } from '~/actions';
import { STATUS } from '~/literals';

import { ChangePasswordFormValidationMessage, ChangePasswordParams, RootState, UserState } from '~/types';


function SignUp() {
  const dispatch = useDispatch();
  const status = useSelector<RootState, string>(({ user }) => user?.status || STATUS.IDLE);
  const { errorMessage, requestSuccess } = useSelector<RootState, UserState>(({ user }) => user || { errorMessage: '', requestSuccess: false });
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);

  const [validationMode, setValidationMode] = useState(false);
  const [changePasswordParams, setChangePasswordParams] = useState<ChangePasswordParams>({
    password: '',
    passwordConfirm: '',
    token: searchParams.get('request') || '',
  });

  const [validationMessages, setValidationMessages] = useState<ChangePasswordFormValidationMessage>({
    password: [],
    passwordConfirm: [],
  });



  useEffect(() => {
    dispatch(userPageLoaded());
  }, []);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChangePasswordParams = {
      ...changePasswordParams,
      password: event.target.value,
    } as ChangePasswordParams;

    setChangePasswordParams(newChangePasswordParams);
    setValidationMessages(getSearchValidationMessages(newChangePasswordParams));
  };

  const handleCinfirmPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newChangePasswordParams = {
      ...changePasswordParams,
      passwordConfirm: event.target.value,
    } as ChangePasswordParams;

    setChangePasswordParams(newChangePasswordParams);
    setValidationMessages(getSearchValidationMessages(newChangePasswordParams));
  };



  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const messages = getSearchValidationMessages(changePasswordParams);

    setValidationMessages(messages);

    if (messages.passwordConfirm.length || messages.password.length) {
      setValidationMode(true);
    } else {
      dispatch(changePassword(changePasswordParams));
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Change password
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }}>
          {errorMessage ? <Alert sx={{ marginBottom: 2 }} severity="error">{errorMessage}</Alert> : ''}
          {requestSuccess ?
            <Alert sx={{ marginBottom: 2 }} severity="success">
              Password successfully changed.
              <Link style={{ color: 'inherit' }} to="/login">
                Click here to login.
              </Link>
            </Alert> : ''}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                error={validationMode && validationMessages.password.length > 0}
                fullWidth
                helperText={
                  validationMode && validationMessages.password.length > 0
                    ? validationMessages.password.join('\n')
                    : null
                }
                id="password"
                label="Password"
                margin="normal"
                name="password"
                onChange={handlePasswordChange}
                placeholder="Password"
                required
                type="password"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                error={validationMode && validationMessages.passwordConfirm.length > 0}
                fullWidth
                helperText={
                  validationMode && validationMessages.passwordConfirm.length > 0
                    ? validationMessages.passwordConfirm.join('\n')
                    : null
                }
                id="passwordConfirm"
                label="Confirm password"
                margin="normal"
                name="passwordConfirm"
                onChange={handleCinfirmPasswordChange}
                placeholder="Confirm password"
                required
                type="password"
              />
            </Grid>
          </Grid>
          <Button
            disabled={status === STATUS.RUNNING}
            fullWidth
            sx={{ mt: 3, mb: 2 }}
            type="submit"
            variant="contained"
          >
            {status === STATUS.RUNNING ? <CircularProgress color="primary" size={24} /> : 'Change Password'}
          </Button>
          <Grid container justifyContent="flex-end">
            <Grid item>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link to="/login" style={{ cursor: 'pointer' }}>
                Do not want to change your password? Sign in
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Container >
  );
}

export default SignUp;

const getSearchValidationMessages = (fields: ChangePasswordParams): ChangePasswordFormValidationMessage => {
  const validationMessages: ChangePasswordFormValidationMessage = {
    password: [],
    passwordConfirm: [],
  };

  if (!fields.password) {
    validationMessages.password.push('* Please choose a password.');
  }

  if (!fields.passwordConfirm) {
    validationMessages.passwordConfirm.push('* Please enter your password again.');
  }

  if (fields.password !== fields.passwordConfirm) {
    validationMessages.passwordConfirm.push('* Passwords do not match.');
  }

  return validationMessages;
};
